import { styled, Box, InputLabel, Modal, Typography } from '@mui/material';
import UPButton from 'components/button';
import { SectionContainer } from '../styles';

export const PayrollInputLabel = styled(InputLabel)(() => ({
  fontWeight: 600,
}));

export const PayrollAddNewItemButton = styled(UPButton)(() => ({
  margin: '16px 0px',
}));

/** Payroll card: no vertical padding, so PayrollTabContainer alone owns the spacing above and below each tab. */
export const PayrollSectionContainer = styled(SectionContainer)(() => ({
  paddingTop: 0,
  paddingBottom: 0,
}));

/** Root container of every Payroll sub-tab, so they all share the same spacing under the tab bar. */
export const PayrollTabContainer = styled(Box)(() => ({
  paddingTop: '16px',
  paddingBottom: '32px',
  width: '100%',
}));

/** Holds a tab's success/error alerts; takes no space when no alert is shown. */
export const PayrollTabAlerts = styled(Box)(() => ({
  marginTop: '32px',
  '&:empty': {
    display: 'none',
  },
}));

/** Wraps the shared ServiceCodes component (used outside Payroll too) and drops its own top margin. */
export const PayrollServiceCodesTabContainer = styled(PayrollTabContainer)(() => ({
  '& > :first-of-type': {
    marginTop: 0,
  },
}));

/** Add/Delete button for the first row of a Payroll sub-tab; the top gap comes from PayrollTabContainer. */
export const PayrollTabActionButton = styled(PayrollAddNewItemButton)(() => ({
  marginTop: 0,
}));

export const PayrollEditFunctionModal = styled(Modal)(() => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  widht: '400px',
  maxHeight: '500px',
  height: 'auto',
  '.MuiBackdrop-root': {
    boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.05)',
    borderRadius: '8px',
    position: 'relative',
  },
}));

export const PayrollEditFunctionModalContainer = styled(Box)(() => ({
  width: '100%',
  background: 'white',
  padding: '32px',
  boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.05)',
  borderRadius: '8px',
  border: 'none',
}));

export const PayrollEditFunctionModalTitle = styled(Typography)(({ theme }) => ({
  marginBottom: '16px',
  color: theme.palette.secondary.contrastText,
  fontWeight: 600,
  fontSize: 16,
}));

export const PayrollEditFunctionModalInputBox = styled(Box)(() => ({
  marginBottom: '26px',
}));

export const EditModalTogglesRow = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '32px',
  marginBottom: '42px',
}));

export const MfaResetInputBox = styled(PayrollEditFunctionModalInputBox)(() => ({
  marginTop: '42px',
}));

export const CoordinatesInputContainer = styled(Box)(() => ({
  marginTop: '32px',
}));

export const NumberAndPostalBox = styled(Box)(() => ({
  marginBottom: '26px',
  flexDirection: 'row',
  display: 'flex',
  gap: '16px',
}));

export const PayrollEditFunctionModalCloseIcon = styled(Box)(() => ({
  position: 'absolute',
  right: 32,
  top: 32,
  width: 24,
  height: 24,
  cursor: 'pointer',
}));

export const PayrollEditFunctionModalInputContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
}));

export const PayrollEditFunctionModalColumn = styled(Box)(() => ({
  flex: '1 1 100%',
  overflow: 'hidden',
}));

export const STACKED_EDIT_MODAL_BREAKPOINT = 1932;

export const StackedEditModal = styled(PayrollEditFunctionModal)(({ theme }) => ({
  [theme.breakpoints.down(STACKED_EDIT_MODAL_BREAKPOINT)]: {
    maxHeight: '90vh',
  },
}));

export const StackedEditModalContainer = styled(PayrollEditFunctionModalContainer)(({ theme }) => ({
  [theme.breakpoints.down(STACKED_EDIT_MODAL_BREAKPOINT)]: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
  },
}));

export const StackedEditModalScrollArea = styled(PayrollEditFunctionModalInputContainer)(({ theme }) => ({
  [theme.breakpoints.down(STACKED_EDIT_MODAL_BREAKPOINT)]: {
    flexDirection: 'column',
    overflowY: 'auto',
    minHeight: 0,
    maxHeight: '480px',
  },
}));

export const StackedEditModalColumn = styled(PayrollEditFunctionModalColumn)(({ theme }) => ({
  '&:not(:last-of-type)': {
    marginRight: '16px',
  },
  [theme.breakpoints.down(STACKED_EDIT_MODAL_BREAKPOINT)]: {
    // Load-bearing: the base is flex '1 1 100%', which on the vertical axis makes each column SHRINK
    // to fit the scroll area instead of overflowing it - so the fields get squeezed on top of each
    // other and nothing ever scrolls. Stacked, the columns must keep their natural height.
    flex: '0 0 auto',
    '&:not(:last-of-type)': {
      marginRight: 0,
    },
  },
}));

export const PayrollEditFunctionModalActionContainer = styled(Box)(() => ({
  marginTop: '32px',
  display: 'flex',
  justifyContent: 'center',
  gap: '16px',
}));

export const PayrollEditFunctionModalActionButton = styled(UPButton)(() => ({
  width: '100px',
}));

export const CoefficientPerWorkerClassGridContainer = styled(Box)(() => ({
  height: '300px',
  width: '100%',
}));

export const WageComponentsModalStyled = styled(Modal)(({ theme }) => ({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '550px',
  zIndex: '2',
  maxHeight: '600px',
  height: 'auto',
  '.MuiBackdrop-root': {
    boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.05)',
    borderRadius: '8px',
    position: 'relative',
  },
  [theme.breakpoints.up('sm')]: {
    width: '550px',
  },

  [theme.breakpoints.down(611)]: {
    width: '90%',
  },
}));

export const ModalContainer = styled(Box)(() => ({
  width: '100%',
  background: 'white',
  position: 'relative',
  padding: '20px',
  boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.05)',
  borderRadius: '8px',
  border: 'none',
  maxHeight: '650px',
  overflowX: 'auto',
}));

export const ModalHeader = styled(Box)(() => ({
  position: 'sticky',
  top: 0,
  background: 'white',
  padding: '5px 5px 0px 5px',
  display: 'flex',
  justifyContent: 'space-between',
  zIndex: 999,
  borderTopRightRadius: '8px',
  borderTopLeftRadius: '8px',
}));

export const ModalTitle = styled(Typography)(({ theme }) => ({
  marginBottom: '16px',
  color: theme.palette.secondary.contrastText,
  fontWeight: 600,
  fontSize: 16,
  textAlign: 'left',
}));

export const ModalCloseIcon = styled(Box)(() => ({
  width: 24,
  height: 24,
  cursor: 'pointer',
}));

export const WageSetupDataGridContainer = styled(Box)(() => ({
  height: '60vh',
  width: '70vw',
}));

export const AddWageComponentButtonContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: '16px',
}));

export const ErrorText = styled(InputLabel)(({ theme }) => ({
  fontSize: 12,
  '&.MuiFormLabel-colorPrimary': {
    color: theme.palette.error.main,
  },
}));

export const PayrollContainer = styled(Box)(({ theme }) => ({
  width: '70%',
  [theme.breakpoints.down(1620)]: {
    width: '80%',
  },
  [theme.breakpoints.down(1360)]: {
    width: '90%',
  },
  [theme.breakpoints.down(1200)]: {
    width: '95%',
  },
  [theme.breakpoints.down(915)]: {
    width: '100%',
  },
}));
